import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useAuth } from '../auth/AuthContext';

interface SubscriptionStatus {
  isFounding?: boolean;
  foundingDiscountPercent?: number;
  planCode?: string;
}

/**
 * Badge "Founding Member" — aparece no header do app quando o tenant
 * faz parte do programa Founding 10.
 *
 * Por que existe:
 *   - Sinal social interno (membros founding sentem reconhecimento).
 *   - Diferencial em demos pra outros prospects ("essas N clínicas já entraram").
 *   - Cara da equipe interna ver quem é founding sem ir no admin-ops.
 *
 * Reutiliza o cache da query `subscription.status` (mesma queryKey do
 * TrialBanner e SubscriptionSection — sem round-trip extra).
 */
export function FoundingBadge() {
  const { user } = useAuth();
  const isE2E =
    typeof navigator !== 'undefined' && navigator.webdriver === true;

  const { data } = useQuery<SubscriptionStatus>({
    queryKey: ['subscription.status'],
    queryFn: async () => (await api.get('/me/subscription/status')).data,
    staleTime: 60_000,
    retry: 1,
    enabled: !!user && !isE2E,
  });

  if (!data?.isFounding) return null;

  const discount = data.foundingDiscountPercent ?? 0;

  return (
    <span
      data-testid="founding-badge"
      title={
        discount > 0
          ? `Founding Member — ${discount}% de desconto vitalício`
          : 'Founding Member'
      }
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-gradient-to-r from-amber-400 to-amber-500 text-amber-950 border border-amber-300 shadow-sm"
    >
      <svg
        className="w-3 h-3"
        fill="currentColor"
        viewBox="0 0 20 20"
        aria-hidden
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.957a1 1 0 00.951.69h4.162c.969 0 1.371 1.24.588 1.81l-3.367 2.446a1 1 0 00-.364 1.118l1.286 3.957c.299.92-.755 1.688-1.54 1.118L10 14.347l-3.367 2.446c-.784.57-1.838-.197-1.539-1.118l1.286-3.957a1 1 0 00-.364-1.118L2.65 9.154c-.784-.57-.38-1.81.588-1.81h4.162a1 1 0 00.951-.69l1.286-3.957z" />
      </svg>
      Founding
    </span>
  );
}
